// HomePage - Centre Commercial Virtuel du Bâtiment
// Design Ultra Premium - Multi-Univers
// Réorganisé avec optimisation mobile (scroll horizontal)

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Rocket, Star, ArrowRight, ChevronRight, Shield, Clock,
  Wrench, Store, ShoppingBag, Users, Briefcase, FileText, Search,
  Zap, MapPin, CheckCircle2, TrendingUp, Package, Tag, Truck, Award,
  Play, Home, Plus, Video, ShoppingCart, Heart, Eye, MessageCircle,
  Sparkles, Crown, Timer, Percent, Gift, BadgeCheck, Globe, ChevronLeft, Mail,
  Mic, ArrowUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import UnifiedHeader from '../components/layout/UnifiedHeader';
import MainNavigation from '../components/layout/MainNavigation';
import AbraRail from '../components/layout/AbraRail';
import { useTenant } from '../context/TenantContext';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

// ═══════════════════════════════════════════════════════════════════
// COMPOSANTS SCROLL HORIZONTAL MOBILE
// ═══════════════════════════════════════════════════════════════════

// Container pour scroll horizontal sur mobile
function HorizontalScrollSection({ children, className = "" }) {
  return (
    <>
      {/* Desktop: Grille normale */}
      <div className={`hidden sm:grid ${className}`}>
        {children}
      </div>
      {/* Mobile: Scroll horizontal */}
      <div className="sm:hidden overflow-x-auto scrollbar-hide -mx-4 px-4">
        <div className="flex gap-3 pb-2" style={{ width: 'max-content' }}>
          {children}
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CARTES OPTIMISÉES MOBILE
// ═══════════════════════════════════════════════════════════════════

// Carte Artisan - Version compacte mobile (style bannière)
function ArtisanCard({ shop, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="group relative bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#10B981]/30 transition-all hover:shadow-xl
                 w-[280px] sm:w-auto flex-shrink-0"
    >
      {/* Cover */}
      <div className="relative h-24 sm:h-32 overflow-hidden">
        <img 
          src={shop.cover_image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400'} 
          alt={shop.shop_name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        {/* Badge vérifié */}
        {shop.is_verified && (
          <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-emerald-500 rounded-full flex items-center gap-1">
            <BadgeCheck size={10} className="text-white" />
            <span className="text-[9px] font-semibold text-white">Vérifié</span>
          </div>
        )}
        
        {/* Logo */}
        <div className="absolute -bottom-5 left-3">
          <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl border-2 border-white overflow-hidden shadow-lg">
            <img 
              src={shop.logo || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100'} 
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        
        {/* Rating sur l'image mobile */}
        <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-white/90 px-1.5 py-0.5 rounded-full">
          <Star size={10} className="text-yellow-500 fill-yellow-500" />
          <span className="text-[10px] font-bold text-yellow-700">{shop.rating_average?.toFixed(1) || '5.0'}</span>
        </div>
      </div>
      
      {/* Content - Plus compact sur mobile */}
      <div className="pt-6 sm:pt-8 pb-3 sm:pb-4 px-3 sm:px-4">
        <div className="flex items-start justify-between mb-1 sm:mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-sm sm:text-base group-hover:text-[#10B981] transition-colors truncate">
              {shop.shop_name}
            </h3>
            <p className="text-xs sm:text-sm text-[#F97316] font-medium truncate">{shop.metier}</p>
          </div>
        </div>
        
        <p className="text-[11px] sm:text-xs text-gray-500 line-clamp-1 sm:line-clamp-2 mb-2 sm:mb-3">{shop.description}</p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-gray-400">
            <MapPin size={11} className="sm:w-3 sm:h-3" />
            <span className="text-[10px] sm:text-xs">{shop.city}</span>
          </div>
          <Link 
            to={`/artisans/${shop.slug || shop.id}`}
            className="text-[10px] sm:text-xs font-semibold text-[#10B981] hover:underline flex items-center gap-0.5"
          >
            Voir <ArrowRight size={10} className="sm:w-3 sm:h-3" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

// Carte Boutique - Version compacte mobile
function ShopCard({ product, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all group
                 w-[200px] sm:w-auto flex-shrink-0"
    >
      <div className="relative h-24 sm:h-32 overflow-hidden">
        <img 
          src={product.image || product.images?.[0] || 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=300'} 
          alt={product.shop_name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-2 left-2">
          <span className="px-1.5 py-0.5 bg-white/90 rounded-lg text-[10px] sm:text-xs font-semibold text-gray-900">
            {product.shop_name || 'Boutique Pro'}
          </span>
        </div>
        <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-[#1E40AF] rounded-full">
          <span className="text-[8px] sm:text-[10px] font-semibold text-white">NOUVEAU</span>
        </div>
      </div>
      <div className="p-2 sm:p-3">
        <p className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-1">{product.title}</p>
        <p className="text-[10px] sm:text-xs text-gray-500">{product.category}</p>
      </div>
    </motion.div>
  );
}

// Carte Vente Flash - Version compacte mobile
function FlashProductCard({ product, index }) {
  const [timeLeft, setTimeLeft] = useState({ hours: 2, minutes: 30, seconds: 0 });
  
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  
  const discount = product.flash_price ? Math.round((1 - product.flash_price / product.price) * 100) : 30;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.1 }}
      className="relative bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all group
                 w-[180px] sm:w-auto flex-shrink-0"
    >
      {/* Badge promo */}
      <div className="absolute top-2 left-2 z-10 px-1.5 py-0.5 bg-red-500 rounded-full flex items-center gap-0.5">
        <Zap size={10} className="text-white" />
        <span className="text-[10px] font-bold text-white">-{discount}%</span>
      </div>
      
      {/* Timer */}
      <div className="absolute top-2 right-2 z-10 px-1.5 py-0.5 bg-black/70 backdrop-blur-sm rounded-full flex items-center gap-0.5">
        <Timer size={10} className="text-red-400" />
        <span className="text-[9px] font-mono text-white">
          {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}
        </span>
      </div>
      
      {/* Image */}
      <div className="relative h-28 sm:h-40 overflow-hidden">
        <img 
          src={product.image || product.images?.[0] || 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=300'} 
          alt={product.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      
      {/* Content */}
      <div className="p-2 sm:p-4">
        <h3 className="font-semibold text-gray-900 text-xs sm:text-sm line-clamp-1 sm:line-clamp-2 mb-1 sm:mb-2 group-hover:text-[#1E40AF]">
          {product.title}
        </h3>
        <div className="flex items-baseline gap-1 sm:gap-2">
          <span className="text-base sm:text-xl font-bold text-red-600">
            {(product.flash_price || product.price * 0.7).toFixed(0)}€
          </span>
          <span className="text-[10px] sm:text-sm text-gray-400 line-through">
            {product.price?.toFixed(0)}€
          </span>
        </div>
      </div>
    </motion.div>
  );
}

// Carte Produit du Mois - Version compacte mobile
function ProductCard({ product, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-[#1E40AF]/20 hover:shadow-lg transition-all
                 w-[150px] sm:w-auto flex-shrink-0"
    >
      <div className="relative h-24 sm:h-36 overflow-hidden">
        <img 
          src={product.image || product.images?.[0] || 'https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=300'} 
          alt={product.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <button className="absolute top-1.5 right-1.5 w-6 h-6 sm:w-8 sm:h-8 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Heart size={12} className="text-gray-600 sm:w-4 sm:h-4" />
        </button>
      </div>
      <div className="p-2 sm:p-3">
        <h3 className="font-medium text-gray-900 text-[11px] sm:text-sm line-clamp-1 mb-0.5 sm:mb-1">{product.title}</h3>
        <div className="flex items-center justify-between">
          <span className="font-bold text-[#1E40AF] text-xs sm:text-base">{product.price?.toFixed(0)}€</span>
          <span className="text-[9px] sm:text-xs text-gray-400 hidden sm:block">{product.shop_name}</span>
        </div>
      </div>
    </motion.div>
  );
}

// Univers Card - Design Premium Multi-Univers
function UniversCard({ univers, index }) {
  const Icon = univers.icon;
  
  return (
    <Link to={univers.path}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        whileHover={{ y: -8, boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}
        className="relative h-64 rounded-3xl overflow-hidden cursor-pointer group"
        style={{ background: univers.gradient }}
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        
        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-between p-6">
          <div>
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Icon size={28} className="text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">{univers.title}</h3>
            <p className="text-white/80 text-sm">{univers.description}</p>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-white/70 text-sm">{univers.count}</span>
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
              <ArrowRight size={20} className="text-white" />
            </div>
          </div>
        </div>
      </motion.div>
    </Link>
  );
}

// Section Header avec effet premium
function SectionHeader({ icon: Icon, title, subtitle, link, linkText, color = '#1E40AF' }) {
  return (
    <div className="flex items-end justify-between mb-4 sm:mb-6">
      <div className="flex items-center gap-2 sm:gap-3">
        <div 
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon size={20} className="sm:w-6 sm:h-6" style={{ color }} />
        </div>
        <div>
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900">{title}</h2>
          {subtitle && <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">{subtitle}</p>}
        </div>
      </div>
      {link && (
        <Link to={link} className="flex items-center gap-1 text-xs sm:text-sm font-semibold hover:underline" style={{ color }}>
          <span className="hidden sm:inline">{linkText || 'Voir tout'}</span>
          <span className="sm:hidden">Tout</span>
          <ArrowRight size={14} className="sm:w-4 sm:h-4" />
        </Link>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE - ORDRE RÉORGANISÉ
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// DONNÉES DE TEST STATIQUES POUR PRÉOUVERTURE
// ═══════════════════════════════════════════════════════════════════

const MOCK_ARTISANS = [
  {
    id: 'art-1',
    shop_name: 'Plomberie Martin',
    metier: 'Plombier',
    city: 'Lyon',
    rating_average: 4.9,
    is_verified: true,
    cover_image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
    logo: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100'
  },
  {
    id: 'art-2',
    shop_name: 'Élec Pro',
    metier: 'Électricien',
    city: 'Paris',
    rating_average: 4.8,
    is_verified: true,
    cover_image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=400',
    logo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100'
  },
  {
    id: 'art-3',
    shop_name: 'Maçonnerie Dupont',
    metier: 'Maçon',
    city: 'Marseille',
    rating_average: 4.7,
    is_verified: true,
    cover_image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400',
    logo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100'
  },
  {
    id: 'art-4',
    shop_name: 'Carrelage Concept',
    metier: 'Carreleur',
    city: 'Nice',
    rating_average: 5.0,
    is_verified: true,
    cover_image: 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=400',
    logo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100'
  },
];

const MOCK_FLASH_PRODUCTS = [
  {
    id: 'flash-1',
    title: 'Perceuse Bosch Pro 18V',
    price: 299,
    flash_price: 199,
    image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=400',
    category: 'Outillage'
  },
  {
    id: 'flash-2',
    title: 'Kit Plomberie Complet',
    price: 150,
    flash_price: 89,
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400',
    category: 'Plomberie'
  },
  {
    id: 'flash-3',
    title: 'Disqueuse Makita 125mm',
    price: 189,
    flash_price: 129,
    image: 'https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=400',
    category: 'Outillage'
  },
  {
    id: 'flash-4',
    title: 'Niveau Laser Dewalt',
    price: 450,
    flash_price: 299,
    image: 'https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?w=400',
    category: 'Mesure'
  },
];

const MOCK_PRODUCTS = [
  {
    id: 'prod-1',
    title: 'Carrelage Gris 60x60',
    price: 29,
    image: 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=400',
    category: 'Carrelage'
  },
  {
    id: 'prod-2',
    title: 'Peinture Blanche 10L',
    price: 45,
    image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=400',
    category: 'Peinture'
  },
  {
    id: 'prod-3',
    title: 'Câble Électrique 100m',
    price: 89,
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400',
    category: 'Électricité'
  },
  {
    id: 'prod-4',
    title: 'Tube PVC 100mm',
    price: 12,
    image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=400',
    category: 'Plomberie'
  },
  {
    id: 'prod-5',
    title: 'Parquet Chêne Massif',
    price: 65,
    image: 'https://images.unsplash.com/photo-1616401784845-180882ba9ba8?w=400',
    category: 'Revêtement'
  },
];

export default function HomePage({ embedded = false }) {
  const tenant = useTenant(); // porte d'entrée active (multi-domaines)
  const navigate = useNavigate();
  const [shops, setShops] = useState(MOCK_ARTISANS);
  const [products, setProducts] = useState(MOCK_PRODUCTS);
  const [flashSales, setFlashSales] = useState(MOCK_FLASH_PRODUCTS);
  const [loading, setLoading] = useState(false);
  const [searchTab, setSearchTab] = useState('projet');
  
  // State pour la barre de saisie Abra sur la homepage
  const [homeInputValue, setHomeInputValue] = useState('');
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showInputBar, setShowInputBar] = useState(true);
  
  // Cacher la barre de saisie quand on scrolle vers le bas (après section Abra)
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      // Cacher après 350px de scroll
      setShowInputBar(scrollY < 350);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // State pour le champ métier avec autocomplétion
  const [metierInput, setMetierInput] = useState('');
  const [showMetierDropdown, setShowMetierDropdown] = useState(false);
  
  // Liste des 15 métiers pour l'autocomplétion
  const METIERS = [
    "Plombier", "Électricien", "Maçon", "Charpentier",
    "Couvreur", "Menuisier", "Peintre", "Carreleur",
    "Plaquiste", "Chauffagiste", "Serrurier", "Vitrier",
    "Paysagiste", "Architecte", "Entreprise générale"
  ];
  
  // Filtrer les métiers selon l'input
  const filteredMetiers = METIERS.filter(m =>
    m.toLowerCase().includes(metierInput.toLowerCase())
  );
  
  // Les 6 Univers
  const UNIVERS = [
    {
      id: 'artisans',
      title: 'Artisans',
      description: 'Trouve des professionnels certifiés près de chez toi',
      icon: Wrench,
      path: '/artisans',
      gradient: 'linear-gradient(135deg, #10B981, #059669)',
      count: 'Artisans vérifiés'
    },
    {
      id: 'marketplace',
      title: 'Marketplace',
      description: 'Matériaux et outillage sans commission',
      icon: Store,
      path: '/produits',
      gradient: 'linear-gradient(135deg, #14285A, #1E40AF)',
      count: '0% commission'
    },
    {
      id: 'annonces',
      title: 'Entre Particuliers',
      description: 'Petites annonces matériaux et outillage',
      icon: ShoppingBag,
      path: '/annonces',
      gradient: 'linear-gradient(135deg, #F97316, #FB923C)',
      count: 'Annonces gratuites'
    },
    {
      id: 'reseau',
      title: 'Réseau Social',
      description: 'La communauté des pros du bâtiment',
      icon: Users,
      path: '/reseau',
      gradient: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
      count: 'Réseau pro'
    },
    {
      id: 'emploi',
      title: 'Emploi',
      description: 'Offres d\'emploi du secteur BTP',
      icon: Briefcase,
      path: '/emploi',
      gradient: 'linear-gradient(135deg, #EC4899, #F472B6)',
      count: 'Offres BTP'
    },
    {
      id: 'reseau-pros',
      title: 'Réseau Pros',
      description: 'Centrale d\'achat dédiée aux professionnels du bâtiment',
      icon: Building2,
      path: '/reseau-pros',
      gradient: 'linear-gradient(135deg, #0891B2, #06B6D4)',
      count: 'Centrale d\'achat'
    }
  ];

  // Liste des univers pour la barre de filtres horizontale
  const UNIVERS_FILTERS = [
    { id: 'batiment', label: 'Bâtiment', officialName: 'AbracadaBati', active: true },
    { id: 'maison', label: 'Maison', officialName: 'AbracadaHome', active: false },
    { id: 'voyage', label: 'Voyage', officialName: 'AbracadaTravel', active: false },
    { id: 'beaute', label: 'Beauté', officialName: 'AbracadaBeauty', active: false },
    { id: 'mode', label: 'Mode', officialName: 'AbracadaFashion', active: false },
    { id: 'ecologie', label: 'Écologie', officialName: 'AbracadaGreen', active: false },
    { id: 'rencontres', label: 'Rencontres', officialName: 'AbracadaMatch', active: false },
    { id: 'evenements', label: 'Événements', officialName: 'AbracadaEvent', active: false },
    { id: 'animaux', label: 'Animaux', officialName: 'AbracadaZoo', active: false },
    { id: 'hightech', label: 'High-Tech', officialName: 'AbracadaTech', active: false },
    { id: 'immobilier', label: 'Immobilier', officialName: 'AbracadaRealty', active: false },
    { id: 'adultes', label: 'Adultes 18+', officialName: 'AbracadaSecret', active: false },
    { id: 'sport', label: 'Sport', officialName: 'AbracadaSport', active: false },
    { id: 'alimentation', label: 'Alimentation', officialName: 'AbracadaFood', active: false },
    { id: 'auto', label: 'Auto & Moto', officialName: 'AbracadaCar', active: false },
    { id: 'nautisme', label: 'Nautisme', officialName: 'AbracadaBoat', active: false },
    { id: 'sante', label: 'Santé', officialName: 'AbracadaHealth', active: false },
    { id: 'enfants', label: 'Enfants', officialName: 'AbracadaToys', active: false },
    { id: 'formation', label: 'Formation', officialName: 'AbracadaLearn', active: false },
    { id: 'art', label: 'Art & Culture', officialName: 'AbracadaArt', active: false },
  ];
  
  // État pour l'univers sélectionné
  const [selectedUnivers, setSelectedUnivers] = useState('batiment');
  const [bientotMessage, setBientotMessage] = useState(null);
  
  // Handler pour clic sur univers
  const handleUniversFilterClick = (univers) => {
    if (univers.active) {
      setSelectedUnivers(univers.id);
      setBientotMessage(null);
    } else {
      // Afficher message "Bientôt" sous la barre avec le nom officiel
      setBientotMessage(univers.officialName);
      // Masquer après 2.5 secondes
      setTimeout(() => setBientotMessage(null), 2500);
    }
  };

  useEffect(() => {
    // Pas besoin de fetch - utiliser les données mockées
    // Les données de test sont déjà chargées dans le state initial
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="homepage" style={{ paddingBottom: '80px' }}>
      {/* CSS responsive Mobile + Desktop */}
      <style>{`
        /* Hero image: positionnement par défaut (desktop) */
        .mobile-hero-img {
          object-position: center 18%;
        }
        
        /* Mobile: cadrage hero ajusté pour montrer le bâtiment complet avec entrée */
        @media (max-width: 767px) {
          .mobile-hero-img {
            object-position: center 55% !important;
          }
        }
        
        /* Samsung/petits écrans (< 420px) - Optimisation */
        @media (max-width: 419px) {
          #homepage-container {
            padding-bottom: 64px !important;
          }
        }
        
        /* Mobile petits écrans (Crosscall/CAT ≤375px) */
        @media (max-width: 375px) {
          .mobile-hero-img {
            height: 190px !important;
            object-position: center 55% !important;
          }
          .mobile-social-zone {
            padding: 6px 12px !important;
          }
          .mobile-social-line1 {
            font-size: 10px !important;
          }
          .mobile-social-line2 {
            font-size: 11px !important;
          }
          .mobile-card-desc {
            font-size: 12px !important;
          }
          .mobile-card-input {
            font-size: 11px !important;
          }
          .mobile-card-btn {
            font-size: 13px !important;
            padding: 11px !important;
          }
        }
        .mobile-proof-arrow:hover {
          transform: translateX(3px);
        }
        .desktop-proof-arrow:hover {
          transform: translateX(4px);
        }
        /* Desktop glass card placeholder */
        @media (min-width: 1024px) {
          .desktop-glass-input::placeholder {
            color: rgba(255,255,255,0.5);
          }
        }
      `}</style>
      {/* Header Unifié — Masquer barre de recherche sur home (mobile + desktop) */}
      {!embedded && <UnifiedHeader hideSearchOnMobile={true} hideSearchOnDesktop={true} globeLinksToWorld={true} />}

      {/* Rail Abra — porte abracadabati.com : Abracada replié à gauche (desktop) */}
      {!embedded && tenant.abraRail && <AbraRail />}

      {/* Navigation Principale — Masquée sur mobile/tablette pour la HomePage */}
      {!embedded && (
        <div className="hidden lg:block">
          <MainNavigation />
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          HERO - Centre Commercial du Bâtiment
      ═══════════════════════════════════════════════════════════════════ */}
      
      {/* ═══════════════════════════════════════════════════════════════════
          VERSION MOBILE (<768px) — Interface Abra sur fond bleu
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="md:hidden" style={{ marginTop: '-52px', backgroundColor: '#1a2a4a' }}>
        {/* IMAGE CENTRE COMMERCIAL — hauteur fixe 200px */}
        <div className="relative w-full" style={{ backgroundColor: '#1a2a4a' }}>
          <img
            src="/images/centre-commercial-abracadabati.webp"
            alt="AbracadaBati — 1er Centre Commercial Virtuel du Bâtiment"
            loading="eager"
            fetchPriority="high"
            className="w-full block mobile-hero-img"
            style={{ 
              width: '100%',
              height: '200px',
              objectFit: 'cover',
              margin: 0, 
              padding: 0, 
              borderRadius: 0,
              display: 'block',
            }}
          />
          {/* Dégradé bas — fond vers #1a2a4a */}
          <div 
            className="absolute bottom-0 left-0 right-0 pointer-events-none"
            style={{
              height: '80px',
              background: 'linear-gradient(to bottom, transparent 0%, rgba(26, 42, 74, 0.65) 45%, #1a2a4a 100%)',
            }}
          />
        </div>
        
        {/* ═══════════════════════════════════════════════════════════════════
            INTERFACE ABRA — Sur fond bleu (contenu scrollable)
        ═══════════════════════════════════════════════════════════════════ */}
        <div 
          className="home-abra-section"
          style={{ 
            backgroundColor: '#1a2a4a', 
            paddingTop: '0px', 
            paddingLeft: '16px', 
            paddingRight: '16px', 
            paddingBottom: '80px',
            minHeight: 'calc(100vh - 280px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          
          {/* Contenu principal */}
          <div>
          
          {/* Bonjour toi ! */}
          <h2 
            className="text-center font-bold mb-1"
            style={{ 
              color: '#FFFFFF', 
              fontSize: '24px',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Bonjour toi !
          </h2>
          
          {/* Sous-titre */}
          <p 
            className="text-center mb-5"
            style={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px',
              lineHeight: 1.5,
            }}
          >
            Appuie sur le micro pour demander à Abra
          </p>
          
          {/* Boutons d'action - Grille */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'compte-rendu', label: 'Compte rendu', color: '#FFF5EB', iconColor: '#E8955C', icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E8955C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" x2="12" y1="19" y2="22"/>
                </svg>
              )},
              { id: 'creer-devis', label: 'Créer un devis', color: '#EBF5FF', iconColor: '#3B82F6', icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
              )},
              { id: 'agenda', label: 'Mon agenda', color: '#EBF5FF', iconColor: '#3B82F6', icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                  <text x="12" y="17" textAnchor="middle" fontSize="8" fill="#3B82F6" fontWeight="bold">17</text>
                </svg>
              )},
              { id: 'prospects', label: 'Prospects', color: '#ECFDF5', iconColor: '#10B981', icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              )},
              { id: 'boite-mail', label: 'Boîte mail', color: '#EBF5FF', iconColor: '#3B82F6', icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                </svg>
              )},
              { id: 'messages', label: 'Messages', color: '#F5F3FF', iconColor: '#8B5CF6', icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  <path d="M8 10h.01"/><path d="M12 10h.01"/><path d="M16 10h.01"/>
                </svg>
              )},
            ].map((action) => (
              <button
                key={action.id}
                onClick={() => navigate('/cockpit-particulier')}
                className="flex flex-col items-center justify-center p-2.5 rounded-xl transition-all hover:scale-[1.02]"
                style={{ 
                  backgroundColor: action.color,
                  minHeight: '70px',
                }}
                data-testid={`home-action-${action.id}`}
              >
                <div className="mb-1">{action.icon}</div>
                <span style={{ fontSize: '10px', fontWeight: 600, color: '#1F2937', lineHeight: 1.2, textAlign: 'center' }}>
                  {action.label}
                </span>
              </button>
            ))}
          </div>
          
          {/* ═══ BARRE DE SAISIE — DANS LE CONTENU ═══ */}
          <div className="relative mt-auto pt-6">
            <div 
              className="flex items-center rounded-full"
              style={{ 
                height: '52px',
                paddingLeft: '16px',
                paddingRight: '8px',
                backgroundColor: '#ececec',
                border: '2px solid #F97316'
              }}
            >
              <button
                onClick={() => setShowAttachMenu(!showAttachMenu)}
                className="flex-shrink-0 flex items-center justify-center"
                style={{ color: '#676767' }}
              >
                <Plus size={24} strokeWidth={2} />
              </button>
              
              <input
                data-testid="home-chat-input"
                type="text"
                value={homeInputValue}
                onChange={(e) => setHomeInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && homeInputValue.trim()) {
                    sessionStorage.setItem('cockpit_initial_message', homeInputValue.trim());
                    sessionStorage.setItem('cockpit_hide_sidebar', 'true');
                    navigate('/cockpit-particulier');
                  }
                }}
                placeholder="Écris ta demande..."
                className="flex-1 bg-transparent border-none outline-none shadow-none appearance-none"
                style={{
                  marginLeft: '4px',
                  marginRight: '8px',
                  fontSize: '16px',
                  fontWeight: '400',
                  color: '#1a1a1a',
                  caretColor: '#1a1a1a'
                }}
              />
              
              <button
                data-testid="home-btn-voice"
                onClick={() => {
                  sessionStorage.setItem('cockpit_action', 'open_vocalia');
                  sessionStorage.setItem('cockpit_hide_sidebar', 'true');
                  navigate('/cockpit-particulier');
                }}
                className="flex-shrink-0"
                style={{ marginLeft: '8px', color: '#9a9a9a' }}
              >
                <Mic size={24} strokeWidth={1.5} />
              </button>
              
              <button
                data-testid="home-btn-send"
                onClick={() => {
                  if (homeInputValue.trim()) {
                    sessionStorage.setItem('cockpit_initial_message', homeInputValue.trim());
                  }
                  sessionStorage.setItem('cockpit_hide_sidebar', 'true');
                  navigate('/cockpit-particulier');
                }}
                className="flex-shrink-0 rounded-full flex items-center justify-center text-white"
                style={{ width: '40px', height: '40px', backgroundColor: '#F97316' }}
              >
                <ArrowUp size={20} strokeWidth={2.5} />
              </button>
            </div>
          </div>
          </div>
        </div>
      </section>
      
      {/* ═══ MENU ATTACHMENTS MOBILE — POSITION FIXE (popup seul) ═══ */}
      <AnimatePresence>
        {showAttachMenu && (
          <div className="md:hidden">
            {/* Overlay */}
            <div 
              className="fixed inset-0 z-[100]" 
              style={{ backgroundColor: 'transparent' }}
              onClick={() => setShowAttachMenu(false)}
            />
            {/* Menu popup */}
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="fixed left-4 w-56 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-[101]"
              style={{ bottom: '132px' }}
            >
              <div className="p-2">
                <div className="flex items-center justify-between px-3 py-1.5">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Importer
                  </p>
                  <button
                    onClick={() => setShowAttachMenu(false)}
                    className="w-6 h-6 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                  >
                    <span className="text-gray-400 text-sm">×</span>
                  </button>
                </div>
                <button
                  onClick={() => {
                    setShowAttachMenu(false);
                    sessionStorage.setItem('cockpit_action', 'upload_photo');
                    sessionStorage.setItem('cockpit_hide_sidebar', 'true');
                    navigate('/cockpit-particulier');
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-800">Photos</p>
                    <p className="text-xs text-gray-500">Images, captures</p>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setShowAttachMenu(false);
                    sessionStorage.setItem('cockpit_action', 'upload_doc');
                    sessionStorage.setItem('cockpit_hide_sidebar', 'true');
                    navigate('/cockpit-particulier');
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                    <FileText size={16} className="text-red-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-800">Documents</p>
                    <p className="text-xs text-gray-500">PDF, Word, Excel</p>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setShowAttachMenu(false);
                    sessionStorage.setItem('cockpit_action', 'upload_video');
                    sessionStorage.setItem('cockpit_hide_sidebar', 'true');
                    navigate('/cockpit-particulier');
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9333ea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2.18"/><path d="m10 8 6 4-6 4V8Z"/></svg>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-800">Vidéos</p>
                    <p className="text-xs text-gray-500">MP4, MOV</p>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setShowAttachMenu(false);
                    sessionStorage.setItem('cockpit_action', 'upload_other');
                    sessionStorage.setItem('cockpit_hide_sidebar', 'true');
                    navigate('/cockpit-particulier');
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-800">Autres fichiers</p>
                    <p className="text-xs text-gray-500">Tous types</p>
                  </div>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      
      {/* ═══════════════════════════════════════════════════════════════════
          VERSION TABLETTE (768-1024px)
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="hidden md:block lg:hidden">
        {/* IMAGE CENTRE COMMERCIAL — Tablette */}
        <div className="relative w-full" style={{ backgroundColor: '#1E3A5F' }}>
          <img
            src="/images/centre-commercial-abracadabati.webp"
            alt="AbracadaBati — 1er Centre Commercial Virtuel du Bâtiment"
            loading="eager"
            fetchPriority="high"
            className="w-full block"
            style={{ 
              height: 'auto',
              maxHeight: '420px',
              objectFit: 'cover',
              objectPosition: 'center 15%',
              margin: 0, 
              padding: 0, 
              borderRadius: 0,
            }}
          />
          {/* Dégradé bas tablette */}
          <div 
            className="absolute bottom-0 left-0 right-0 pointer-events-none"
            style={{
              height: '140px',
              background: 'linear-gradient(transparent 0%, rgba(30, 58, 95, 0.7) 50%, #1E3A5F 100%)',
            }}
          />
        </div>
        
        {/* Zone bleue avec preuve sociale tablette */}
        <div style={{ backgroundColor: '#1E3A5F', padding: '16px 20px 20px', margin: 0 }}>
          {/* Bloc preuve sociale tablette */}
          <a 
            href="/a-propos"
            className="proof-block-tablet block text-center mb-4"
            style={{
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'opacity 0.15s ease',
            }}
          >
            <span
              className="proof-tablet-line1"
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.90)',
                display: 'block',
                lineHeight: 1.5,
                marginBottom: '2px',
              }}
            >
              +300 000 personnes ont suivi 3 ans de construction.
            </span>
            <span
              className="proof-tablet-line2"
              style={{
                fontSize: '14px',
                fontWeight: 700,
                color: '#FFFFFF',
                display: 'block',
              }}
            >
              Aujourd'hui — Bienvenue chez toi.{' '}
              <span 
                className="proof-tablet-arrow"
                style={{ 
                  color: '#F97316', 
                  fontWeight: 700, 
                  display: 'inline-block',
                  transition: 'transform 0.15s ease',
                }}
              >
                →
              </span>
            </span>
          </a>
          
          {/* Carte centrale tablette */}
          <div 
            className="bg-white overflow-hidden mx-auto"
            style={{ 
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              maxWidth: '600px',
            }}
          >
            {/* Onglets tablette */}
            <div className="flex" style={{ borderBottom: '0.5px solid #e5e7eb' }}>
              {[
                { id: 'projet', label: 'projet' },
                { id: 'artisan', label: 'artisan' },
                { id: 'acheter', label: 'Acheter' },
              ].map((tab) => {
                const isActive = searchTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setSearchTab(tab.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 transition-all"
                    style={{
                      padding: '14px 8px',
                      color: isActive ? '#E8650A' : '#6b7280',
                      fontWeight: isActive ? 600 : 400,
                      fontSize: '14px',
                      borderBottom: isActive ? '2px solid #E8650A' : '2px solid transparent',
                    }}
                  >
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
            
            {/* Contenu onglet projet tablette */}
            {searchTab === 'projet' && (
              <div className="p-5">
                <p className="text-center text-gray-600 text-sm mb-4">
                  Décris ton projet — Reçois jusqu'à 5 devis d'artisans certifiés en 24h
                </p>
                <input
                  type="text"
                  placeholder="Ex: Rénovation salle de bain, installation électrique..."
                  className="w-full mb-3 focus:outline-none focus:ring-2 focus:ring-[#F97316]/20"
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    padding: '14px',
                    fontSize: '14px',
                  }}
                />
                <button 
                  onClick={() => navigate('/decrire-projet')}
                  className="w-full text-white font-bold"
                  style={{
                    background: '#F97316',
                    borderRadius: '10px',
                    padding: '14px',
                    fontSize: '15px',
                  }}
                >
                  Obtenir mes devis gratuits
                </button>
              </div>
            )}
            
            {searchTab === 'artisan' && (
              <div className="p-5">
                <p className="text-center text-gray-600 text-sm mb-4">
                  Trouve un artisan certifié près de chez toi
                </p>
                <input
                  type="text"
                  placeholder="Quel métier ? (plombier, électricien...)"
                  className="w-full mb-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    padding: '14px',
                    fontSize: '14px',
                  }}
                />
                <button 
                  onClick={() => navigate('/artisans')}
                  className="w-full text-white font-bold"
                  style={{
                    background: '#10B981',
                    borderRadius: '10px',
                    padding: '14px',
                    fontSize: '15px',
                  }}
                >
                  Trouver un artisan
                </button>
              </div>
            )}
            
            {searchTab === 'acheter' && (
              <div className="p-5">
                <p className="text-center text-gray-600 text-sm mb-4">
                  Matériaux et outillage au meilleur prix
                </p>
                <input
                  type="text"
                  placeholder="Ex: Carrelage, perceuse, peinture..."
                  className="w-full mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    padding: '14px',
                    fontSize: '14px',
                  }}
                />
                <button 
                  onClick={() => navigate('/produits')}
                  className="w-full text-white font-bold"
                  style={{
                    background: '#1E40AF',
                    borderRadius: '10px',
                    padding: '14px',
                    fontSize: '15px',
                  }}
                >
                  Voir le marketplace
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* ═══════════════════════════════════════════════════════════════════
          VERSION DESKTOP (>1024px) — Hero plein écran avec carte structurée
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="hidden lg:block">
        {/* CONTENEUR HERO — position relative, overflow hidden */}
        <div 
          style={{ 
            position: 'relative', 
            overflow: 'hidden',
            width: '100%',
            height: '100vh',
          }}
        >
          {/* IMAGE HERO — 100vh, cover, center 35% */}
          <img
            src="/images/centre-commercial-abracadabati.webp"
            alt="AbracadaBati — 1er Centre Commercial Virtuel du Bâtiment"
            loading="eager"
            fetchPriority="high"
            style={{ 
              width: '100%',
              height: '100vh',
              objectFit: 'cover',
              objectPosition: 'center 35%',
              display: 'block',
            }}
          />
          
          {/* GRADIENT CINÉMATIQUE — z-index 1 */}
          <div 
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to bottom, transparent 50%, rgba(0, 0, 0, 0.45) 100%)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
          
          {/* COMPTEUR FONDATEURS — z-index 2, forme pilule */}
          <div
            style={{
              position: 'absolute',
              top: 'calc(50% - 85px)',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
              background: 'rgba(0, 0, 0, 0.55)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              borderRadius: '999px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Texte */}
            <span
              style={{
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
                padding: '10px 28px 8px',
              }}
            >
              🔥 <span style={{ color: '#E8650A', fontWeight: 700 }}>100</span> places Fondateur restantes sur 100
            </span>
            {/* Barre de progression — pleine largeur en bas */}
            <div
              style={{
                width: '100%',
                height: '6px',
                background: 'rgba(255,255,255,0.25)',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  background: '#E8650A',
                }}
              />
            </div>
          </div>
          
          {/* CARTE FLOTTANTE — z-index 2 */}
          <div 
            style={{ 
              position: 'absolute',
              top: 'calc(50% + 65px)',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '500px',
              maxWidth: '90vw',
              zIndex: 2,
              borderRadius: '12px',
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.3)',
              borderTop: '3px solid #E8650A',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.35), 0 8px 20px rgba(0, 0, 0, 0.20)',
              background: 'rgba(255, 255, 255, 0.92)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
          >
            {/* ONGLETS */}
            <div 
              className="flex"
              style={{ 
                borderBottom: '1px solid #f3f4f6',
              }}
            >
              {[
                { id: 'projet', label: 'projet', icon: FileText },
                { id: 'artisan', label: 'artisan', icon: Wrench },
                { id: 'acheter', label: 'Acheter', icon: ShoppingCart },
              ].map((tab) => {
                const Icon = tab.icon;
                const isActive = searchTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setSearchTab(tab.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 transition-all relative"
                    style={{
                      padding: '10px 12px',
                      fontSize: '13px',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? '#E8650A' : '#9ca3af',
                      borderBottom: isActive ? '2px solid #E8650A' : '2px solid transparent',
                    }}
                  >
                    <Icon size={13} style={{ color: isActive ? '#E8650A' : '#9ca3af' }} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
            
            {/* CONTENU */}
            <div
              style={{
                padding: '10px 16px 14px',
              }}
            >
              {/* Contenu onglet PROJET */}
              {searchTab === 'projet' && (
                <div>
                  <p 
                    className="text-center"
                    style={{ 
                      fontSize: '13px', 
                      color: '#374151',
                      marginBottom: '8px',
                    }}
                  >
                    Décris ton projet — Reçois jusqu'à 5 devis d'artisans certifiés en 24h
                  </p>
                  <input
                    type="text"
                    placeholder="Ex: Rénovation salle de bain, installation électrique..."
                    className="w-full focus:outline-none focus:ring-2 focus:ring-[#F97316]/20"
                    style={{
                      background: '#FFFFFF',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '12px',
                      boxSizing: 'border-box',
                      marginBottom: '8px',
                      width: '100%',
                    }}
                  />
                  <button 
                    onClick={() => navigate('/decrire-projet')}
                    className="w-full text-white font-bold flex items-center justify-center gap-2"
                    style={{
                      background: '#F97316',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 4px 16px rgba(249,115,22,0.4)',
                      width: '100%',
                    }}
                  >
                    <FileText size={14} />
                    Obtenir mes devis gratuits
                  </button>
                </div>
              )}
              
              {/* Contenu onglet ARTISAN */}
              {searchTab === 'artisan' && (
                <div>
                  <p 
                    className="text-center"
                    style={{ 
                      fontSize: '13px', 
                      color: '#374151',
                      marginBottom: '8px',
                    }}
                  >
                    Trouve un artisan certifié près de chez toi
                  </p>
                  <input
                    type="text"
                    placeholder="Quel métier ? (plombier, électricien...)"
                    className="w-full focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                    style={{
                      background: '#FFFFFF',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '12px',
                      boxSizing: 'border-box',
                      marginBottom: '8px',
                      width: '100%',
                    }}
                  />
                  <button 
                    onClick={() => navigate('/artisans')}
                    className="w-full text-white font-bold flex items-center justify-center gap-2"
                    style={{
                      background: '#10B981',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 4px 16px rgba(16,185,129,0.4)',
                      width: '100%',
                    }}
                  >
                    <Wrench size={14} />
                    Trouver un artisan
                  </button>
                </div>
              )}
              
              {/* Contenu onglet ACHETER */}
              {searchTab === 'acheter' && (
                <div>
                  <p 
                    className="text-center"
                    style={{ 
                      fontSize: '13px', 
                      color: '#374151',
                      marginBottom: '8px',
                    }}
                  >
                    Matériaux et outillage au meilleur prix
                  </p>
                  <input
                    type="text"
                    placeholder="Ex: Carrelage, perceuse, peinture..."
                    className="w-full focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    style={{
                      background: '#FFFFFF',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '12px',
                      boxSizing: 'border-box',
                      marginBottom: '8px',
                      width: '100%',
                    }}
                  />
                  <button 
                    onClick={() => navigate('/produits')}
                    className="w-full text-white font-bold flex items-center justify-center gap-2"
                    style={{
                      background: '#1E40AF',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '14px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 4px 16px rgba(30,64,175,0.4)',
                      width: '100%',
                    }}
                  >
                    <Search size={14} />
                    Voir le marketplace
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          1. EXPLOREZ NOS UNIVERS (Juste après Hero - Mobile + Desktop)
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-8 sm:py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6 sm:mb-10">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-[#1E40AF]/10 rounded-full text-[#1E40AF] text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              <Globe size={14} className="sm:w-4 sm:h-4" />
              6 Univers • 1 Plateforme
            </span>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
              Explorez nos univers
            </h2>
            <p className="text-gray-600 text-sm sm:text-base max-w-xl mx-auto">
              Tout ce dont tu as besoin pour tes projets de construction et rénovation
            </p>
          </div>
          
          {/* Mobile: Grille 2x3 compacte */}
          <div className="grid grid-cols-2 gap-3 sm:hidden">
            {UNIVERS.map((univers, index) => {
              const Icon = univers.icon;
              return (
                <Link key={univers.id} to={univers.path}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="relative h-28 rounded-2xl overflow-hidden"
                    style={{ background: univers.gradient }}
                  >
                    <div className="absolute inset-0 p-3 flex flex-col justify-between">
                      <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                        <Icon size={18} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-white">{univers.title}</h3>
                        <p className="text-[10px] text-white/70">{univers.count}</p>
                      </div>
                    </div>
                  </motion.div>
                </Link>
              );
            })}
          </div>
          
          {/* Desktop: Grille 3 colonnes */}
          <div className="hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-6">
            {UNIVERS.map((univers, index) => (
              <UniversCard key={univers.id} univers={univers} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          2. MEILLEURS ARTISANS DE LA SEMAINE
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-8 sm:py-12 px-4 bg-gradient-to-r from-emerald-50 to-teal-50">
        <div className="max-w-6xl mx-auto">
          <SectionHeader 
            icon={Award}
            title="Meilleurs Artisans de la Semaine"
            subtitle="Les professionnels les mieux notés par nos clients"
            link="/artisans"
            color="#10B981"
          />
          
          {/* Scroll horizontal sur une ligne */}
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <div className="flex gap-3 pb-2" style={{ width: 'max-content' }}>
              {shops.map((shop, index) => (
                <ArtisanCard key={shop.id} shop={shop} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          2. POURQUOI VENDRE SUR ABRACADABATI (4 Cards Réassurance Vendeur)
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-8 sm:py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6 sm:mb-10">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full text-emerald-700 text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              <Store size={14} className="sm:w-4 sm:h-4" />
              Pour les vendeurs
            </span>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
              Pourquoi vendre sur AbracadaBati ?
            </h2>
            <p className="text-sm sm:text-base text-gray-600">La plateforme qui change la donne pour les pros du bâtiment</p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {[
              { 
                icon: Users, 
                title: 'Vendre sans intermédiaire', 
                desc: 'Vente directe entre professionnels et clients. Aucun tiers, aucune plateforme intrusive.', 
                color: '#10B981' 
              },
              { 
                icon: Percent, 
                title: '0% de commission', 
                desc: 'Contrairement aux plateformes classiques (~15%). Aucun frais sur les ventes.', 
                color: '#1E40AF' 
              },
              { 
                icon: Gift, 
                title: 'Outils gratuits & CRM', 
                desc: 'CRM complet (devis, factures, suivi client). Gestion commerciale incluse gratuitement.', 
                color: '#F97316' 
              },
              { 
                icon: Shield, 
                title: 'Encaissement sécurisé', 
                desc: 'Paiements sécurisés via compte séquestre. Garantie d\'être payé.', 
                color: '#8B5CF6' 
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100 hover:shadow-lg hover:border-emerald-200 transition-all group"
              >
                <div 
                  className="w-10 h-10 sm:w-14 sm:h-14 mb-3 sm:mb-4 rounded-xl sm:rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: `${item.color}15` }}
                >
                  <item.icon size={20} className="sm:w-7 sm:h-7" style={{ color: item.color }} />
                </div>
                <h3 className="font-bold text-gray-900 text-sm sm:text-base mb-1 sm:mb-2">{item.title}</h3>
                <p className="text-[11px] sm:text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          3. VOUS ÊTES ARTISAN ? REJOIGNEZ LES FONDATEURS - MOBILE UNIQUEMENT
          (Desktop a déjà le CTA dans la barre orange en haut)
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="sm:hidden py-10 px-4 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-emerald-500/20 rounded-full filter blur-[80px]" />
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-[#F97316]/20 rounded-full filter blur-[60px]" />
        </div>
        
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium mb-4 sm:mb-6">
              <Rocket size={16} />
              Programme Fondateur 2025-2026
            </span>
            
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
              Vous êtes artisan ?
              <span className="block text-emerald-400 mt-1 sm:mt-2">Rejoignez les fondateurs</span>
            </h2>
            
            <p className="text-base sm:text-lg text-slate-300 mb-6 sm:mb-8 px-4">
              Bénéficiez de <span className="text-white font-semibold">1 an offert</span> et participez à la construction 
              de la première plateforme 100% dédiée aux pros du bâtiment
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link to="/offre-fondateur">
                <Button className="h-12 sm:h-14 px-6 sm:px-8 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold text-base sm:text-lg w-full sm:w-auto">
                  <Crown size={20} className="mr-2" />
                  Devenir Fondateur
                </Button>
              </Link>
              <Link to="/compte">
                <Button variant="outline" className="h-12 sm:h-14 px-6 sm:px-8 border-2 border-white/30 text-white hover:bg-white/10 rounded-xl font-semibold w-full sm:w-auto">
                  En savoir plus
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          4. VENTES FLASH (Scroll horizontal mobile)
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-8 sm:py-12 px-4 bg-gradient-to-r from-red-50 to-orange-50">
        <div className="max-w-6xl mx-auto">
          <SectionHeader 
            icon={Zap}
            title="Ventes Flash"
            subtitle="Offres limitées dans le temps"
            link="/produits?flash=true"
            color="#EF4444"
          />
          
          {/* Mobile: Scroll horizontal sur une ligne */}
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <div className="flex gap-3 pb-2" style={{ width: 'max-content' }}>
              {flashSales.map((product, index) => (
                <FlashProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          5. PRODUITS DU MOIS (Scroll horizontal mobile)
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-8 sm:py-12 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <SectionHeader 
            icon={Award}
            title="Produits du Mois"
            subtitle="Sélection de nos meilleures offres"
            link="/produits"
            color="#F97316"
          />
          
          {/* Mobile: Scroll horizontal sur une ligne */}
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <div className="flex gap-3 pb-2" style={{ width: 'max-content' }}>
              {products.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          7. POUR LES ACHETEURS (Réassurance)
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-8 sm:py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6 sm:mb-10">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 rounded-full text-blue-700 text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              <ShoppingCart size={14} className="sm:w-4 sm:h-4" />
              Pour les acheteurs
            </span>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">
              Pourquoi acheter sur AbracadaBati ?
            </h2>
            <p className="text-sm sm:text-base text-gray-600">Une expérience d'achat unique et sécurisée</p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {[
              { 
                icon: Users, 
                title: 'Achat sans intermédiaire', 
                desc: 'Achat direct auprès des vendeurs. Prix plus justes, sans surcoût.', 
                color: '#1E40AF' 
              },
              { 
                icon: Percent, 
                title: '0% de commission', 
                desc: 'Contrairement aux plateformes classiques (~15%). Pas de surcoût caché.', 
                color: '#10B981' 
              },
              { 
                icon: BadgeCheck, 
                title: 'Artisans certifiés', 
                desc: 'Professionnels vérifiés et qualifiés. Qualité et fiabilité garanties.', 
                color: '#F97316' 
              },
              { 
                icon: Shield, 
                title: 'Paiement sécurisé', 
                desc: 'Paiement via compte séquestre. Sécurité totale jusqu\'à la livraison.', 
                color: '#8B5CF6' 
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100 hover:shadow-lg hover:border-blue-200 transition-all group"
              >
                <div 
                  className="w-10 h-10 sm:w-14 sm:h-14 mb-3 sm:mb-4 rounded-xl sm:rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: `${item.color}15` }}
                >
                  <item.icon size={20} className="sm:w-7 sm:h-7" style={{ color: item.color }} />
                </div>
                <h3 className="font-bold text-gray-900 text-sm sm:text-base mb-1 sm:mb-2">{item.title}</h3>
                <p className="text-[11px] sm:text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          8. NEWSLETTER
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-10 sm:py-16 px-4 bg-gradient-to-r from-[#1E40AF] to-[#3B82F6]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-2xl bg-white/20 flex items-center justify-center">
              <Mail size={28} className="text-white sm:w-8 sm:h-8" />
            </div>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2 sm:mb-3">
              Restez informé
            </h2>
            <p className="text-white/80 text-sm sm:text-base mb-6 sm:mb-8">
              Recevez les dernières actualités, offres exclusives et conseils du bâtiment
            </p>
            
            <form className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
              <input
                type="email"
                placeholder="Votre adresse email"
                className="flex-1 h-12 sm:h-14 px-5 bg-white rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
              <Button className="h-12 sm:h-14 px-6 sm:px-8 bg-[#F97316] hover:bg-[#EA580C] rounded-xl font-semibold">
                S'inscrire
              </Button>
            </form>
            
            <p className="text-white/60 text-xs mt-4">
              Pas de spam, désabonnement en un clic
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          FOOTER
      ═══════════════════════════════════════════════════════════════════ */}
      <footer className="bg-slate-900 text-white">
        {/* Main Footer */}
        <div className="max-w-6xl mx-auto px-4 py-10 sm:py-16">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
            {/* Logo & Description */}
            <div className="col-span-2 sm:col-span-1">
              <Link to="/abracadaworld" className="flex items-center gap-2 mb-4">
                <img 
                  src="/logo-abracadaworld-transparent.png" 
                  alt="AbracadaWorld"
                  className="h-12 w-auto"
                />
              </Link>
              <p className="text-slate-400 text-sm mb-4">
                Le 1er centre commercial virtuel. 21 univers thématiques. Sans commission.
              </p>
              {/* Badge Beta */}
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full text-amber-400 text-xs font-medium">
                <Sparkles size={12} />
                Version BETA - Test
              </span>
            </div>
            
            {/* Navigation */}
            <div>
              <h4 className="font-semibold text-white mb-4">Navigation</h4>
              <ul className="space-y-2">
                <li><Link to="/artisans" className="text-slate-400 hover:text-white text-sm transition-colors">Artisans</Link></li>
                <li><Link to="/produits" className="text-slate-400 hover:text-white text-sm transition-colors">Marketplace</Link></li>
                <li><Link to="/particuliers" className="text-slate-400 hover:text-white text-sm transition-colors">Petites annonces</Link></li>
                <li><Link to="/emploi" className="text-slate-400 hover:text-white text-sm transition-colors">Emploi</Link></li>
                <li><Link to="/reseau" className="text-slate-400 hover:text-white text-sm transition-colors">Réseau social</Link></li>
              </ul>
            </div>
            
            {/* Pour les Pros */}
            <div>
              <h4 className="font-semibold text-white mb-4">Pour les Pros</h4>
              <ul className="space-y-2">
                <li><Link to="/offre-fondateur" className="text-slate-400 hover:text-white text-sm transition-colors">Programme Fondateur</Link></li>
                <li><Link to="/compte" className="text-slate-400 hover:text-white text-sm transition-colors">Espace Pro</Link></li>
                <li><Link to="/reseau-pros" className="text-slate-400 hover:text-white text-sm transition-colors">Réseau Pros</Link></li>
                <li><Link to="/decrire-projet" className="text-slate-400 hover:text-white text-sm transition-colors">Décrire un projet</Link></li>
              </ul>
            </div>
            
            {/* Légal */}
            <div>
              <h4 className="font-semibold text-white mb-4">Informations</h4>
              <ul className="space-y-2">
                <li><Link to="/mentions-legales" className="text-slate-400 hover:text-white text-sm transition-colors">Mentions légales</Link></li>
                <li><Link to="/cgv" className="text-slate-400 hover:text-white text-sm transition-colors">CGV</Link></li>
                <li><Link to="/cgu" className="text-slate-400 hover:text-white text-sm transition-colors">CGU</Link></li>
                <li><Link to="/politique-confidentialite" className="text-slate-400 hover:text-white text-sm transition-colors">Confidentialité</Link></li>
                <li><Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</Link></li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar - Avertissement Beta */}
        <div className="border-t border-slate-800">
          <div className="max-w-6xl mx-auto px-4 py-4 sm:py-6">
            {/* Avertissement Beta */}
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles size={16} className="text-amber-400" />
                </div>
                <div>
                  <p className="text-amber-400 font-semibold text-sm mb-1">Version BETA - Test</p>
                  <p className="text-slate-400 text-xs leading-relaxed">
                    AbracadaBati est actuellement en version Beta. <strong className="text-amber-300">Aucune transaction réelle n'est possible pour le moment.</strong> Les fonctionnalités de paiement et de vente seront activées lors de l'ouverture officielle prévue le 1er Juillet 2026.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Copyright */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
              <p className="text-slate-500 text-xs">
                © 2025-2026 AbracadaWorld. Tous droits réservés.
              </p>
              <p className="text-slate-500 text-xs">
                Site en version Beta - Aucune transaction disponible
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Footer Spacer for mobile */}
      <div className="h-20 lg:hidden" />
      
      {/* Navigation mobile gérée globalement par MobileNavWrapper dans App.js */}
      
      {/* Scrollbar hide style + hover proof block */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .proof-block-link:hover .proof-line1,
        .proof-block-link:hover .proof-line2 {
          color: rgba(255, 255, 255, 0.95) !important;
        }
        .proof-block-link:hover .proof-arrow {
          color: #E8650A !important;
          transform: translateX(3px);
        }
        .proof-block-tablet:hover .proof-tablet-arrow {
          transform: translateX(3px);
        }
        .proof-block-desktop:hover .proof-desktop-line1 {
          color: rgba(255, 255, 255, 0.95) !important;
        }
        .proof-block-desktop:hover .proof-desktop-arrow {
          transform: translateX(4px);
        }
      `}</style>
    </div>
  );
}
